import { Upload, Form, message } from 'antd'
import React, { useMemo, useEffect, useCallback, useRef } from 'react'
import { UploadOutlined } from '@ant-design/icons';
import { DIAN_SHANG_YE_WU_API_BASE } from '../config/api';

function UploadPic(props) {
    const {
        label,
        name,
        required,
        formStyle,
        maxCount = 10,
        disabled,
        style,
        defaultFileList,
        fileStr,
        onFileChange, // 回调函数，传递文件名数组和文件数组
        onChange // 兼容 onChange，与 onFileChange 功能相同
    } = props;

    // 获取文件名
    const getFileName = (item) => {
        return item?.name || item?.response?.name || item?.url?.split('/').pop() || '';
    };

    const normalizedDefaultFileList = useMemo(() => {
        if (!Array.isArray(defaultFileList)) {
            return [];
        }
        // 确保每个文件对象都是有效的，并且有必要的属性
        return defaultFileList
            .filter(file => {
                // 确保 file 是对象，并且有 url 或 uid 属性
                return file && typeof file === 'object' && (file.url || file.uid || file.name);
            })
            .map((file, index) => {
                // 确保每个文件对象都有必要的属性
                // 如果已有 uid，使用它；否则基于 url 或 name 生成稳定的 uid
                const stableUid = file.uid || 
                    (file.url ? `file-${file.url}` : `file-${file.name || index}`) ||
                    `file-${index}`;
                
                return {
                    uid: stableUid,
                    name: file.name || file.url?.split('/').pop() || '文件',
                    status: file.status || 'done',
                    url: file.url || '',
                    response: file.response || null,
                    ...file
                };
            });
    }, [defaultFileList]);

    // 更新父组件的回调
    const updateParentData = useCallback((fileList) => {
        if (!fileList || !Array.isArray(fileList)) return;

        const fileNames = fileList.map(item => getFileName(item)).filter(Boolean);
        
        // 获取文件路径，如果是数组则扁平化
        const fileUrls = fileList.reduce((acc, item) => {
            if (item.response) {
                const result = item.response.Result;
                if (Array.isArray(result)) {
                    // 如果是数组，将所有元素添加到结果中
                    acc.push(...result.filter(Boolean));
                } else if (result) {
                    acc.push(result);
                }
            } else if (item.url) {
                // 处理 URL：移除路径前缀，保留文件名
                // 根据 setupProxy.js，文件路径可能是：
                // - /api/assets/images/application/文件名
                // - /NeiZhangApi/assets/images/application/文件名
                // - http://dianshangtaizhang.zzddww.com/assets/images/application/文件名
                // - http://fxapi.zzddww.com/assets/images/application/文件名
                let url = item.url;
                if (url.includes("/api/assets/images/application/")) {
                    url = url.replace("/api/assets/images/application/", "");
                } else if (url.includes("/DianShangYeWuApi/assets/images/application/")) {
                    url = url.replace("/DianShangYeWuApi/assets/images/application/", "");
                } else if (url.includes("/assets/images/application/")) {
                    url = url.replace("/assets/images/application/", "");
                } else if (url.startsWith("http://") || url.startsWith("https://")) {
                    // 如果是完整 URL，提取文件名
                    url = url.split("/").pop();
                }
                if (url) {
                    acc.push(url);
                }
            }
            return acc;
        }, []);
        
        // 兼容旧的 fileStr 回调
        if (fileStr) {
            const fileStrValue = fileUrls.join(",");
            fileStr(fileStrValue);
        }

        // 新的回调：传递文件名数组和服务器返回的文件路径数组
        // 同时支持 onFileChange 和 onChange
        // 第一个参数：文件名数组（用于显示）
        // 第二个参数：服务器返回的文件路径数组（扁平化的字符串数组）
        if (onFileChange) {
            onFileChange(fileNames, fileUrls);
        }
        if (onChange) {
            onChange(fileNames, fileUrls);
        }
    }, [fileStr, onFileChange, onChange]);

    const homeChange = (e) => {
        if (!e || !e.fileList) return;

        const safeFileList = Array.isArray(e.fileList) ? e.fileList : [];

        // 受控 Upload 挂载/同步时常出现「无 e.file + fileList 为空」，不得据此写表单（会把回填清空）
        if (!e.file && safeFileList.length === 0) {
            return;
        }

        // 必须在 early return 之后再置位，否则「忽略空 onChange」会一直卡住 isUpdatingFromHomeChangeRef
        isUpdatingFromHomeChangeRef.current = true;

        if (e.file) {
            const status = e.file.status;
            if (status === 'done' || status === 'removed' || status === 'error') {
                form?.setFieldsValue({ [name]: safeFileList });
                updateParentData(safeFileList);
            }
        } else {
            form?.setFieldsValue({ [name]: safeFileList });
            updateParentData(safeFileList);
        }
    };

    const form = Form.useFormInstance();
    
    // 监听表单字段变化
    const fileListFromFormRaw = Form.useWatch(name);
    
    // 确保 fileListFromForm 始终是数组
    const fileListFromForm = useMemo(() => {
        if (Array.isArray(fileListFromFormRaw)) {
            return fileListFromFormRaw;
        }
        return [];
    }, [fileListFromFormRaw]);
    
    // 使用 ref 跟踪是否已经初始化过，避免重复设置
    const initializedRef = useRef(false);
    const prevDefaultSigRef = useRef('');

    // defaultFileList（接口回填）变化时允许再次同步到表单，否则关闭弹窗后再打开不会写入
    useEffect(() => {
        const sig = normalizedDefaultFileList.map((f) => `${f.uid}__${f.url || ''}`).join('|');
        if (sig !== prevDefaultSigRef.current) {
            prevDefaultSigRef.current = sig;
            initializedRef.current = false;
        }
    }, [normalizedDefaultFileList]);

    // 当 defaultFileList 变化且表单字段为空时，同步到表单字段
    useEffect(() => {
        if (normalizedDefaultFileList.length > 0 && !initializedRef.current) {
            const currentValue = form?.getFieldValue(name);
            if (!currentValue || !Array.isArray(currentValue) || currentValue.length === 0) {
                // 使用 setTimeout 确保在下一个事件循环中设置，避免与表单初始化冲突
                setTimeout(() => {
                    form?.setFieldsValue({ [name]: normalizedDefaultFileList });
                    initializedRef.current = true;
                }, 0);
            } else {
                initializedRef.current = true;
            }
        }
    }, [normalizedDefaultFileList, name, form]);
    
    // 确定要显示的文件列表：优先使用表单字段值，否则使用默认文件列表
    const displayFileList = useMemo(() => {
        try {
            // 选择要使用的文件列表
            const sourceList = (fileListFromForm && fileListFromForm.length > 0) 
                ? fileListFromForm 
                : normalizedDefaultFileList;
            
            // 确保是数组
            if (!Array.isArray(sourceList)) {
                return [];
            }
            
            // 验证并规范化每个文件对象
            return sourceList
                .filter(file => {
                    // 确保 file 是对象，并且不是 null
                    return file && typeof file === 'object' && !Array.isArray(file);
                })
                .map((file, index) => {
                    try {
                        // 确保每个文件对象都有必要的属性
                        // 如果已有 uid，使用它；否则基于 url 或 name 生成稳定的 uid
                        const stableUid = file.uid || 
                            (file.url ? `file-${String(file.url).replace(/[^a-zA-Z0-9]/g, '-')}` : `file-${file.name || index}`) ||
                            `file-${index}`;
                        
                        return {
                            uid: String(stableUid),
                            name: String(file.name || file.url?.split('/').pop() || '文件'),
                            status: file.status || 'done',
                            url: file.url ? String(file.url) : '',
                            response: file.response || null,
                            ...file
                        };
                    } catch (error) {
                        console.error('Error processing file object:', error, file);
                        // 返回一个基本的有效文件对象
                        return {
                            uid: `file-error-${index}`,
                            name: '文件',
                            status: 'error',
                            url: '',
                            response: null
                        };
                    }
                });
        } catch (error) {
            console.error('Error processing displayFileList:', error);
            return [];
        }
    }, [fileListFromForm, normalizedDefaultFileList]);
    
    // 使用 ref 跟踪上一次的文件列表，避免重复调用
    const prevDisplayFileListRef = useRef(null);
    const isUpdatingFromHomeChangeRef = useRef(false);
    
    // 当文件列表变化时更新父组件数据（仅用于初始化或外部更新，不是用户操作）
    useEffect(() => {
        // 如果是从 homeChange 触发的更新，跳过这个 effect
        if (isUpdatingFromHomeChangeRef.current) {
            // 在下一个事件循环中重置标记
            setTimeout(() => {
                isUpdatingFromHomeChangeRef.current = false;
            }, 0);
            return;
        }
        
        // 只有当文件列表真正改变时才更新父组件
        const hasChanged = JSON.stringify(prevDisplayFileListRef.current) !== JSON.stringify(displayFileList);
        if (hasChanged) {
            prevDisplayFileListRef.current = displayFileList;
            if (displayFileList && Array.isArray(displayFileList) && displayFileList.length > 0) {
                updateParentData(displayFileList);
            } else if (!displayFileList || displayFileList.length === 0) {
                updateParentData([]);
            }
        }
    }, [displayFileList, updateParentData]);

    // 处理文件预览
    const handlePreview = useCallback((file) => {
        // 获取文件 URL
        let previewUrl = file.url || file.thumbUrl;
        
        // 如果文件有 response.Result，构建完整路径
        if (file.response && file.response.Result) {
            let serverFileName = file.response.Result;
            
            // 确保 serverFileName 是字符串
            if (Array.isArray(serverFileName)) {
                // 如果是数组，取第一个元素
                serverFileName = serverFileName[0];
            }
            serverFileName = String(serverFileName || '');
            
            if (serverFileName) {
                if (serverFileName.startsWith('/') || serverFileName.startsWith('/')) {
                    previewUrl = serverFileName;
                } else if (serverFileName.startsWith('/')) {
                    // 已经是相对路径
                    if (!serverFileName.startsWith('/api/') && !serverFileName.startsWith('NeiZhangApi/')) {
                        previewUrl = `/api${serverFileName}`;
                    } else {
                        previewUrl = serverFileName;
                    }
                } else {
                    previewUrl = `http://dianshangtaizhang.zzddww.com/assets/images/application/${serverFileName}`;
                }
            }
        }
        
        // 确保 previewUrl 是有效的字符串
        if (!previewUrl || typeof previewUrl !== 'string') {
            console.error('Invalid preview URL:', previewUrl);
            return;
        }
        
  
        
        // 所有文件都在新窗口打开
        window.open(previewUrl, '_blank');
    }, []);

    return (
        <Form.Item 
            label={label}                         
            valuePropName="fileList"
            getValueFromEvent={(e) => {
                try {
                    if (Array.isArray(e)) {
                        return e;
                    }
                    if (e && e.fileList && Array.isArray(e.fileList)) {
                        return e.fileList;
                    }
                    return [];
                } catch (error) {
                    console.error('Error in getValueFromEvent:', error);
                    return [];
                }
            }}
            normalize={(value) => {
                // 确保表单字段值始终是数组
                if (!value) {
                    return [];
                }
                if (Array.isArray(value)) {
                    return value;
                }
                return [];
            }}
            name={name}
            required={required}
            style={{ marginBottom: 0, ...formStyle }}
            wrapperCol={props.wrapperCol}
        >
            <Upload
                name="image_file[]"
                action={`${DIAN_SHANG_YE_WU_API_BASE}/multiple_upload_image`}
                withCredentials
                listType="text"
                className="upload-list-inline"
                beforeUpload={(file) => {
                    if (file.size === 0) {
                        message.error('不能上传空文件（文件大小为0）');
                        return Upload.LIST_IGNORE;
                    }
                    return true;
                }}
                onChange={homeChange}
                onPreview={handlePreview}
                maxCount={maxCount}
                disabled={disabled}
                style={{ display: 'block', width: 'fit-content', ...style }}
                showUploadList={{showPreviewIcon: true, showRemoveIcon: !disabled, showDownloadIcon: false}}
            >
                <UploadOutlined style={{fontSize: '20px',color: '#1890ff',cursor: 'pointer'}} />
            </Upload>
        </Form.Item>
    );
}
export default UploadPic